'use client'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { createClient } from '@/auth/client'
import { cn } from '@/lib/utils'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { useState, useEffect } from 'react'
import { TURNSTILE_SIGNUP_SITE_KEY, FEATURE_TURNSTILE_SIGNUP } from '@/config'
import { getInvisibleTurnstileToken } from '@/lib/turnstile/client'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { GlassSection } from '@/components/ui/GlassSection'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useInAppBrowser } from '@/hooks/useInAppBrowser'
import { InAppBrowserNotice } from './InAppBrowserNotice'

type SignUpFormProps = React.ComponentPropsWithoutRef<'div'> & {
  onAuthSuccess?: () => void
  variant?: 'page' | 'dialog'
}

export function SignUpForm({
  className,
  onAuthSuccess,
  variant = 'page',
  ...props
}: SignUpFormProps) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isOAuthLoading, setIsOAuthLoading] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()

  // Hide Google OAuth in in-app browsers (Google blocks it) or when ?method=email
  const signInMethod = searchParams?.get('method')
  const { isInAppBrowser, browserType } = useInAppBrowser()
  const isEmailOnly = signInMethod === 'email' || isInAppBrowser === true

  useEffect(() => {
    const urlError = searchParams?.get('error') ?? null
    const urlErrorDescription = searchParams?.get('error_description') ?? null

    if (urlError) {
      setError(urlErrorDescription || urlError)
    }
  }, [searchParams])

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading || isOAuthLoading) return

    setIsLoading(true)
    setError(null)

    if (password !== repeatPassword) {
      setError(t('auth.error_password_mismatch', 'Passwords do not match'))
      setIsLoading(false)
      return
    }

    // Optional Turnstile verification for signup (enabled via feature flag reuse)
    if (FEATURE_TURNSTILE_SIGNUP && TURNSTILE_SIGNUP_SITE_KEY) {
      try {
        const token = await getInvisibleTurnstileToken(TURNSTILE_SIGNUP_SITE_KEY, FEATURE_TURNSTILE_SIGNUP);
        if (!token) {
          setError(t('auth.error_verification_failed', 'Verification failed. Please try again.'));
          setIsLoading(false);
          return;
        }
      } catch {
        setError(t('auth.error_verification_failed', 'Verification failed. Please try again.'));
        setIsLoading(false);
        return;
      }
    }

    const supabase = createClient()

    let emailRedirectTo = `${window.location.origin}/auth/callback`
    try {
      const pendingQuery = window.localStorage.getItem('lema_pending_chat_query')
      if (pendingQuery) {
        const redirectPath = `/chat?query=${encodeURIComponent(pendingQuery)}`
        emailRedirectTo += `?redirect_to=${encodeURIComponent(redirectPath)}`
      }
    } catch {}

    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo
      }
    })

    if (signUpError) {
      setError(signUpError.message)
      setIsLoading(false)
      return
    }

    if (data.user && !data.session) {
      onAuthSuccess?.()
      // Redirect to verify-email page with the user's email
      router.push(`/auth/verify-email?email=${encodeURIComponent(email)}`)
    } else if (data.session) {
      onAuthSuccess?.()
      router.push('/chat')
    }
  }

  const handleSocialSignUp = async () => {
    if (isLoading || isOAuthLoading) return
    
    setIsOAuthLoading(true)
    setError(null)

    const supabase = createClient()

    let redirectTo = `${window.location.origin}/auth/callback`
    try {
      const pendingQuery = window.localStorage.getItem('lema_pending_chat_query')
      if (pendingQuery) {
        const redirectPath = `/chat?query=${encodeURIComponent(pendingQuery)}`
        redirectTo += `?redirect_to=${encodeURIComponent(redirectPath)}`
      }
    } catch {}

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo
      }
    })

    if (error) {
      setError(t('auth.error_google_signup_failed', 'Failed to initiate Google sign-up. Please try again.'))
      setIsOAuthLoading(false)
    }
  }

  const isDialogVariant = variant === 'dialog'
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center p-4',
        isDialogVariant ? 'min-h-0 bg-dark' : 'min-h-screen bg-background',
        className
      )}
      {...props}
    >
      <div className={cn('w-full max-w-md', isDialogVariant && 'max-h-[85vh] overflow-y-auto')}>
        <GlassSection
          title={<span className="text-white">{t('auth.signup_title', 'Start for Free')}</span>}
          level={1}
          align="center"
          headingClassName="mb-3 md:mb-4"
          panelVariant={isDialogVariant ? 'solid' : 'glass'}
        >
          <div className="-mt-2 mb-4 text-center">
            <p className="mt-3 mb-2 text-center text-sm text-neutral-200 font-normal leading-relaxed max-w-xs mx-auto tracking-wide">
              {t('auth.signup_subtitle')}
            </p>
            <p className="text-xs text-neutral-400">
              {t('auth.already_have_account', 'Already have an account?')}{' '}
              <Link href="/auth/sign-in" className="underline hover:text-neutral-200">
                {t('auth.sign_in', 'Sign In')}
              </Link>
            </p>
          </div>

          {/* Only show Google OAuth if not email-only mode */}
          {!isEmailOnly && (
            <>
              {/* Reserve space during hydration to prevent layout shift */}
              {isInAppBrowser === null ? (
                <div className="w-full h-[60px] bg-white/10 animate-pulse rounded-lg" />
              ) : (
                <Button
                  variant="outline"
                  size="lg"
                  type="button"
                  className="w-full bg-white border-0 text-black hover:bg-white/90 py-6 text-base font-medium"
                  onClick={handleSocialSignUp}
                  disabled={isLoading || isOAuthLoading}
                >
                  {isOAuthLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('auth.connecting', 'Connecting...')}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      {t('auth.continue_with_google', 'Continue with Google')}
                    </>
                  )}
                </Button>
              )}
              <div className="my-4">
                <div className="flex justify-center text-xs uppercase">
                  <span className="text-white px-2">{t('auth.or', 'Or')}</span>
                </div>
              </div>
            </>
          )}
          <div className="pt-2">
          <form onSubmit={handleSignUp} className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email" className="text-white">{t('auth.email_label', 'Email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('auth.email_placeholder', 'you@example.com')}
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                onInvalid={(e: React.InvalidEvent<HTMLInputElement>) => {
                  const target = e.target as HTMLInputElement
                  target.setCustomValidity(t('auth.field_required'))
                }}
                onInput={(e: React.FormEvent<HTMLInputElement>) => {
                  const target = e.target as HTMLInputElement
                  target.setCustomValidity('')
                }}
                disabled={isLoading || isOAuthLoading}
                variant="glass"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password" className="text-white">{t('auth.password_label', 'Password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t('auth.password_placeholder', '********')}
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                onInvalid={(e: React.InvalidEvent<HTMLInputElement>) => {
                  const target = e.target as HTMLInputElement
                  target.setCustomValidity(t('auth.field_required'))
                }}
                onInput={(e: React.FormEvent<HTMLInputElement>) => {
                  const target = e.target as HTMLInputElement
                  target.setCustomValidity('')
                }}
                disabled={isLoading || isOAuthLoading}
                variant="glass"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="repeat-password" className="text-white">{t('auth.repeat_password_label', 'Repeat Password')}</Label>
              <Input
                id="repeat-password"
                type="password"
                placeholder={t('auth.repeat_password_placeholder', '********')}
                required
                value={repeatPassword}
                onChange={e => setRepeatPassword(e.target.value)}
                onInvalid={(e: React.InvalidEvent<HTMLInputElement>) => {
                  const target = e.target as HTMLInputElement
                  target.setCustomValidity(t('auth.field_required'))
                }}
                onInput={(e: React.FormEvent<HTMLInputElement>) => {
                  const target = e.target as HTMLInputElement
                  target.setCustomValidity('')
                }}
                disabled={isLoading || isOAuthLoading}
                variant="glass"
              />
            </div>
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Button
              type="submit"
              variant="highlight"
              size="lg"
              className="w-full py-6 text-base font-medium border-0"
              disabled={isLoading || isOAuthLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('auth.creating_account', 'Creating account...')}
                </>
              ) : (
                t('auth.continue_with_email', 'Continue with email')
              )}
            </Button>
            {isInAppBrowser && <InAppBrowserNotice browserType={browserType} />}
          </form>
          </div>
          <p className="mt-4 text-center text-xs text-neutral-400">
            {t('landing.continuation_text', 'By continuing instead')}{' '}
            <Link href="/terms" className="underline hover:text-neutral-200">
              {t('landing.terms_of_service', 'terms of service')}
            </Link>{' '}
            and{' '}
            <Link href="/privacy" className="underline hover:text-neutral-200">
              {t('landing.privacy_policy', 'privacy policy')}
            </Link>
          </p>
        </GlassSection>
      </div>
    </div>
  )
}